import evAPI from '@util/api/ev';
import { M2_TO_SQ_FT, SQ_FT_TO_M2 } from '@util/constants';
import { TFunction } from '@util/types';
import { NamespaceKeys } from 'next-intl';
import { formatNumberToStringWithCommas } from '@util/listings';
import { formatPricePerLivingUnit } from '@util/formatArea';

interface ListingHighlightsData {
  listingData: any;
  demographicsData?: any;
  locale: string;
  preferredMeasurement: string;
  t: TFunction;
}

const mapListingHighlightsData = ({
  listingData,
  demographicsData,
  locale,
  preferredMeasurement,
  t,
}: ListingHighlightsData) => {
  const api = new evAPI();
  const modifiedTaxAnnualAmount = listingData?.TaxAnnualAmount;

  const modifiedLotSizeAcres = listingData?.LotSizeAcres
    ? api.formatNumber({
        number: listingData.LotSizeAcres,
        type: 'decimal',
        locale: locale,
      })
    : undefined;

  const modifiedLotSizeArea = listingData?.LotSizeArea
    ? api.formatNumber({
        number: listingData.LotSizeArea,
        type: 'decimal',
        locale: locale,
      })
    : undefined;

  const modifiedLotSizeUnitsHeader =
    listingData?.LotSizeUnits === 'Square Feet'
      ? 'Sq Ft'
      : listingData?.LotSizeUnits === 'Square Meters'
      ? 'Sq M'
      : listingData?.LotSizeUnits;

  const medianIncome = demographicsData?.average_household_income;

  const percentageOwners = demographicsData?.owner_renter_divide?.owner_percent
    ? api.formatNumber({
        number: demographicsData?.owner_renter_divide?.owner_percent,
        type: 'percent',
        locale: locale,
      })
    : undefined;

  const percentageRenters = demographicsData?.owner_renter_divide?.renter_percent
    ? api.formatNumber({
        number: demographicsData.owner_renter_divide.renter_percent,
        type: 'percent',
        locale: locale,
      })
    : undefined;

  const commutersByCar = demographicsData?.commute_percents?.car
    ? api.formatNumber({
        number: demographicsData.commute_percents.car,
        type: 'percent',
        locale: locale,
      })
    : undefined;

  const commutersByTransit = demographicsData?.commute_percents?.transit
    ? api.formatNumber({
        number: demographicsData.commute_percents.transit,
        type: 'percent',
        locale: locale,
      })
    : undefined;

  const commutersByOther = demographicsData?.commute_percents?.other
    ? api.formatNumber({
        number: demographicsData.commute_percents.other,
        type: 'percent',
        locale: locale,
      })
    : undefined;

  const collegeEducated = demographicsData?.college_educated_percent
    ? api.formatNumber({
        number: demographicsData.college_educated_percent,
        type: 'percent',
        locale: locale,
      })
    : undefined;

  const singlePersonHouseholds = demographicsData?.single_detached_home_percent
    ? api.formatNumber({
        number: demographicsData.single_detached_home_percent,
        type: 'percent',
        locale: locale,
      })
    : undefined;

  const singleFamilyHouseholds = // TODO: shape of data changed, need to update
    demographicsData?.household_composition?.variables
      ?.find((obj) => obj?.variable === 'home_2_ppl')
      ?.value?.toFixed(0) ?? null;

  const multiFamilyHouseholds = // TODO: shape of data changed, need to update
    demographicsData?.household_composition?.variables
      ?.find((obj) => obj?.variable === 'home_3_ppl')
      ?.value?.toFixed(0) ?? null;

  const getLivingArea = () => {
    if (listingData.OriginatingSystemName == 'TREB' && listingData.Custom5) {
      return listingData.Custom5; // For TREB listings, use Custom5 for living area
    } else {
      return listingData.LivingAreaUnits && listingData.LivingAreaUnits === 'Square Meters'
        ? listingData.LivingArea * M2_TO_SQ_FT
        : listingData.LivingArea;
    }
  };

  const livingArea = getLivingArea();

  const livingAreaUnit = listingData.LivingAreaUnits === 'Square Meters' ? 'Sq M' : 'Sq Ft';

  const pricePerUnit = preferredMeasurement === 'm²' ? 'Sq M' : 'Sq Ft';

  const listingHighlightsData = [
    {
      type: 'standard',
      header: t('ListingHighlights.PropertyTab.title'),
      tabListings: [
        {
          header: t('ListingHighlights.PropertyTab.subType'),
          content: listingData.PropertySubType ? t(listingData.PropertySubType) : '-',
          img: '',
        },
        {
          header: t('ListingHighlights.PropertyTab.pricePer', { unit: pricePerUnit }),
          additionalContent: pricePerUnit,
          content:
            listingData.ListPrice && livingArea
              ? formatPricePerLivingUnit(listingData.ListPrice, livingArea, pricePerUnit === 'Sq M')
              : '-',
          img: '',
        },
        {
          header: t('View'),
          content: listingData.View ? listingData.View.map((view) => t(view)).join(', ') : '-',
          img: '',
        },
        listingData.AssociationYN && {
          header: t('Association'),
          content: listingData.AssociationYN ? t('Yes') : t('No'),
          img: '',
        },
        listingData.AssociationFee &&
          listingData.AssociationFeeFrequency && {
            header: t('Association Fee'),
            content: listingData.AssociationFee,
            additionalContent: listingData.AssociationFeeFrequency,
            img: '',
          },
        {
          header: t('Year Built'),
          content: listingData.YearBuilt ? listingData.YearBuilt : '-',
          img: '',
        },
        {
          header: t('ListingHighlights.PropertyTab.architecturalStyle'),
          content: listingData.ArchitecturalStyle
            ? listingData.ArchitecturalStyle.map((style) => t(style)).join(', ')
            : '-',
          img: '',
        },
        {
          header: t('ListingHighlights.PropertyTab.garageSpaces'),
          content: listingData.GarageSpaces ? listingData.GarageSpaces : '-',
          img: '',
        },
        listingData.AttachedGarageYN && {
          header: t('Attached Garage Y/N'),
          content: listingData.AttachedGarageYN ? t('Yes') : t('No'),
          img: '',
        },
        {
          header: t('ListingHighlights.PropertyTab.livingArea', { unit: livingAreaUnit }),
          content: livingArea ? livingArea : '-',
          img: '',
        },
        {
          header: t('Stories'),
          content: listingData.StoriesTotal ? listingData.StoriesTotal : '-',
          img: '',
        },
        {
          header: t('Tax Annual Amount'),
          content: modifiedTaxAnnualAmount ? modifiedTaxAnnualAmount : '-',
          img: '',
        },
        listingData?.LotSizeArea &&
          listingData?.LotSizeUnits && {
            header: `Lot Size (${modifiedLotSizeUnitsHeader})`,
            content: `${modifiedLotSizeArea}`,
            img: '',
          },
        !listingData?.LotSizeArea &&
          !listingData?.LotSizeUnits && {
            header: t('Lot Size Acres'),
            content: modifiedLotSizeAcres ? modifiedLotSizeAcres : '-',
            img: '',
          },
        !listingData?.LotSizeArea &&
          !listingData?.LotSizeUnits && {
            header: t('Lot Size (Sq Ft)'),
            content: listingData.LotSizeSquareFeet ? listingData.LotSizeSquareFeet : '-',
            img: '',
          },
        {
          header: t('Heating'),
          content: listingData.Heating
            ? listingData.Heating.map((heat) => t(heat)).join(', ')
            : '-',
          img: '',
        },
        {
          header: t('Cooling'),
          content: listingData.Cooling
            ? listingData.Cooling.map((cool) => t(cool)).join(', ')
            : '-',
          img: '',
        },
        {
          header: t('Furnished'),
          content: listingData?.Furnished
            ? t(
                `ListingHighlights.PropertyTab.${listingData.Furnished.toLowerCase()}` as NamespaceKeys<
                  string,
                  string
                >,
              )
            : '-',
          img: '',
        },
        {
          header: t('ListingHighlights.PropertyTab.county'),
          content: listingData.CountyOrParish ? listingData.CountyOrParish : '-',
          img: '',
        },
        {
          header: t('ListingHighlights.PropertyTab.subdivision'),
          content: listingData.SubdivisionName ? listingData.SubdivisionName : '-',
          img: '',
        },
        // Only for Honolulu listings
        listingData.StateOrProvince === 'HI' && {
          header: t('Land Tenure'),
          content: listingData.Custom11 ? listingData.Custom11 : '-',
          img: '',
        },
      ].filter((tabListing) => {
        if (listingData.OriginatingSystemName === 'CENTRIS') {
          // CENTRIS does not have COUNTY_OR_PARISH, so remove it for CENTRIS listings
          if (tabListing?.header !== 'County') {
            return tabListing;
          }
        } else {
          return tabListing;
        }
      }),
    },
    {
      type: 'community',
      header: t('Community'),
      tabListings: [
        {
          header: t('Estimated Median Income'),
          content: medianIncome ? medianIncome : '-',
          img: '5dD7iqHjkLHugfNgezQviT',
        },
        {
          header: t('Estimated Owner/Renter Divide'),
          content:
            percentageRenters && percentageOwners
              ? `${percentageRenters} / ${percentageOwners}`
              : '-',
          img: '1MedRTM3hLEvrPYkmC01vt',
        },
        {
          header: t('ListingHighlights.CommunityTab.commuteByCar'),
          content: commutersByCar ? commutersByCar : '-',
          img: '24D4GjooIvvQGVmn4lgJGo',
        },
        {
          header: t('ListingHighlights.CommunityTab.commuteByTransit'),
          content: commutersByTransit ? commutersByTransit : '-',
          img: '1KcNPSTbHA6eR23U6xGCem',
        },
        {
          header: t('ListingHighlights.CommunityTab.commuteByOtherModes'),
          content: commutersByOther ? commutersByOther : '-',
          img: '6mDp8bHvvHEbqVGrcIGXtM',
        },
        {
          header: t('College Educated'),
          content: collegeEducated ? `${collegeEducated}` : '-',
          img: '2ZsVXPZxuZEjkakjiYMRGE',
        },
        {
          header: t('Single Person Households'),
          content: singlePersonHouseholds ? singlePersonHouseholds : '-',
          img: 'pSzFkSgZdY6ly4k9eZ9F0',
        },
        {
          header: t('ListingHighlights.CommunityTab.singleFamilyHouseholds'),
          content: singleFamilyHouseholds ? singleFamilyHouseholds : '-',
          img: '5OhFaZLqQIBVotru6AWX4a',
        },
        {
          header: t('Multi-Family Households'),
          content: multiFamilyHouseholds ? multiFamilyHouseholds : '-',
          img: '4DBzjVfgiTnXC5cr3Y2fNx',
        },
      ],
    },
    {
      type: 'school',
      header: t('ListingHighlights.SchoolTab.title'),
      tabListings: [
        {
          header: t('ListingHighlights.SchoolTab.schoolDistrict'),
          content: listingData.ElementarySchoolDistrict ?? '-',
          img: '',
        },
        {
          header: t('ListingHighlights.SchoolTab.elementarySchool'),
          content: listingData.ElementarySchool ?? '-',
          img: '',
        },
        {
          header: t('ListingHighlights.SchoolTab.middleSchool'),
          content: listingData.MiddleOrJuniorSchool ?? '-',
          img: '',
        },
        {
          header: t('ListingHighlights.SchoolTab.highSchool'),
          content: listingData.HighSchool ?? '-',
          img: '',
        },
      ],
    },
  ];
  return listingHighlightsData;
};

export default mapListingHighlightsData;
